import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { I18nProvider } from "@/i18n/I18nProvider";
import { Nav } from "@/components/festival/Nav";
import { Footer } from "@/components/festival/Footer";
import { getLocationPage } from "@/lib/location.functions";
import { MapPin } from "lucide-react";
import { RichText } from "@/components/admin/RichText";

const SITE_URL = "https://openluchtfilmfestival.nl";

export const Route = createFileRoute("/locatie")({
  head: () => ({
    meta: [
      { title: "Stadspark Berg en Bos — Openluchtfilmfestival Apeldoorn" },
      {
        name: "description",
        content:
          "Alles over onze locatie: Stadspark Berg en Bos in Apeldoorn. Plattegrond, sfeerfoto's en praktische informatie over het festivalterrein.",
      },
      { property: "og:title", content: "Stadspark Berg en Bos — Openluchtfilmfestival" },
      {
        property: "og:description",
        content:
          "Onze thuisbasis: de bosweide van Stadspark Berg en Bos in Apeldoorn. Bekijk de plattegrond en sfeerfoto's.",
      },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/locatie` }],
  }),
  component: LocationPage,
});

function LocationPage() {
  const fetchPage = useServerFn(getLocationPage);
  const { data, isLoading } = useQuery({
    queryKey: ["location-page"],
    queryFn: () => fetchPage(),
  });

  return (
    <I18nProvider lang="nl">
      <div className="min-h-screen bg-background">
        <Nav />
        <main className="pt-28 pb-24 md:pt-36">
          <div className="mx-auto max-w-5xl px-5 md:px-8">
            <p className="font-mono-cinema text-[10px] text-gold">— Locatie</p>
            <h1 className="mt-3 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
              {data?.title ?? "Stadspark Berg en Bos"}
            </h1>
            {data?.intro && (
              <div className="mt-6 max-w-2xl text-lg text-foreground/75">
                <RichText html={data.intro} />
              </div>
            )}

            <a
              href="https://maps.google.com/?q=Stadspark+Berg+en+Bos+Apeldoorn"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-sm text-gold hover:underline"
            >
              <MapPin className="h-4 w-4" /> J.C. Wilslaan, Apeldoorn
            </a>

            {isLoading && (
              <p className="mt-10 text-sm text-foreground/60">Laden…</p>
            )}

            {data?.body && (
              <RichText
                html={data.body}
                className="mt-12 max-w-3xl text-base leading-relaxed text-foreground/80 md:text-lg"
              />
            )}

            {data?.mapUrl && (
              <section className="mt-16">
                <h2 className="font-display text-2xl md:text-3xl">Plattegrond</h2>
                <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card">
                  <img
                    src={data.mapUrl}
                    alt="Plattegrond van het festivalterrein in Stadspark Berg en Bos"
                    loading="lazy"
                    decoding="async"
                    className="h-auto w-full object-contain"
                  />
                </div>
              </section>
            )}

            {data && data.gallery.length > 0 && (
              <section className="mt-16">
                <h2 className="font-display text-2xl md:text-3xl">Impressie</h2>
                <div className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                  {data.gallery.map((g, i) => (
                    <figure
                      key={i}
                      className="overflow-hidden rounded-xl border border-border bg-card"
                    >
                      <img
                        src={g.url}
                        alt={g.caption ?? "Sfeerfoto Stadspark Berg en Bos"}
                        loading="lazy"
                        decoding="async"
                        className="aspect-[4/3] h-full w-full object-cover"
                      />
                      {g.caption && (
                        <figcaption className="p-3 text-xs text-foreground/70">
                          {g.caption}
                        </figcaption>
                      )}
                    </figure>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </I18nProvider>
  );
}